import { BASE_URL } from "@src/utils/environment"
import type { WalletIdentity } from "@src/utils/walletIdentity"

export class TradingSignerLinkApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message)
    this.name = "TradingSignerLinkApiError"
  }
}

// Backoff between attempts. Total: initial + 3 retries = 4 attempts max.
// Tuned for transient infra hiccups, not for slow upstreams; the call is
// already on the user's critical path so the worst-case wall time is
// ~2.3s before we surface the error.
const RETRY_DELAYS_MS = [200, 600, 1500] as const

async function fetchSignerLink(
  walletIdentity: WalletIdentity,
  signal?: AbortSignal
) {
  const response = await fetch(
    `${BASE_URL}/api/trading_signer_links/${encodeURIComponent(walletIdentity)}`,
    { signal }
  )

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new TradingSignerLinkApiError(
      body.error ?? "Failed to fetch trading signer link",
      response.status
    )
  }

  const body = (await response.json()) as {
    raw_id: string
    public_key: string
    address?: string | null
  }
  if (body.address == null) {
    throw new TradingSignerLinkApiError("Trading signer link missing address")
  }

  return {
    rawId: body.raw_id,
    publicKey: body.public_key,
    address: body.address as `0x${string}`,
  }
}

function isRetriable(error: unknown): boolean {
  if (error instanceof TradingSignerLinkApiError) {
    // Retry on 5xx server errors; treat 4xx (incl. 401, 404) as terminal.
    return error.status != null && error.status >= 500
  }
  // Network errors: fetch throws TypeError before getting a Response.
  return error instanceof TypeError
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("aborted", "AbortError"))
      return
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)

    function onAbort() {
      clearTimeout(timer)
      reject(new DOMException("aborted", "AbortError"))
    }

    signal?.addEventListener("abort", onAbort, { once: true })
  })
}

export async function getTradingSignerLink(
  walletIdentity: WalletIdentity,
  signal?: AbortSignal
) {
  let lastError: unknown
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await delay(RETRY_DELAYS_MS[attempt - 1], signal)
    }

    try {
      return await fetchSignerLink(walletIdentity, signal)
    } catch (error) {
      if (signal?.aborted) {
        throw error
      }
      if (!isRetriable(error)) {
        throw error
      }
      lastError = error
    }
  }

  throw lastError
}
